import {
  focusManager,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { setAuthTanstackQuery } from '@teamsparta/auth-frontend';
import { StackProvider } from '@teamsparta/stack-core';
import { toast, Toaster } from '@teamsparta/stack-toast';
import { isClient } from '@teamsparta/utils';
import { Provider as JotaiProvider } from 'jotai';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import { OverlayProvider } from 'overlay-kit';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { OnlineUserInfoProvider } from '@/components/providers/OnlineUserInfoProvider';
import { TokenProvider } from '@/hooks';
import {
  NoneEnrolledException,
  RefreshTokenExpiredException,
  RefreshTokenInvalidException,
  RefreshTokenRevokedException,
} from '@/lib/exceptions';
import { Hackle } from '@/lib/sdk';
import { authStore } from '@/store/user';

const breakpoints = {
  desktop: 1024,
  mobile: 0,
};

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            const errorMessage = query.meta?.errorMessage;
            if (errorMessage) {
              toast.error(errorMessage, { duration: 5000 });
              return;
            }

            if (
              error instanceof RefreshTokenExpiredException ||
              error instanceof RefreshTokenInvalidException ||
              error instanceof RefreshTokenRevokedException ||
              error instanceof NoneEnrolledException
            ) {
              return;
            }

            console.error(error);
          },
        }),
      }),
  );

  useEffect(() => {
    focusManager.setEventListener((handleFocus) => {
      if (isClient() && window.addEventListener) {
        const visibilitychangeHandler = () => {
          handleFocus(document.visibilityState === 'visible');
        };
        const focusHandler = () => {
          handleFocus(true);
        };
        const blurHandler = () => {
          handleFocus(false);
        };

        window.addEventListener('visibilitychange', visibilitychangeHandler);
        window.addEventListener('focus', focusHandler);
        window.addEventListener('blur', blurHandler);
        return () => {
          window.removeEventListener(
            'visibilitychange',
            visibilitychangeHandler,
          );
          window.removeEventListener('focus', focusHandler);
          window.removeEventListener('blur', blurHandler);
        };
      }
    });
  }, []);

  /**
   * @description auth-frontend에서 hh-v2-frontend의 tanstack query를 사용하기 위해 설정
   * @see https://github.com/TeamSparta-Inc/auth-frontend#2-%EC%84%9C%EB%B9%84%EC%8A%A4-%ED%99%98%EA%B2%BD%EC%9D%98-%EB%A6%AC%EC%95%A1%ED%8A%B8-%EC%BF%BC%EB%A6%AC-%EC%A3%BC%EC%9E%85%ED%95%98%EA%B8%B0
   */
  setAuthTanstackQuery({
    useQuery: useQuery,
    useMutation: useMutation,
    useQueryClient: useQueryClient,
  });

  return (
    <StackProvider breakpoints={breakpoints} theme='hanghaeDark'>
      <QueryClientProvider client={queryClient}>
        <JotaiProvider store={authStore}>
          <OnlineUserInfoProvider>
            <TokenProvider>
              <NuqsAdapter>
                <OverlayProvider>
                  <Hackle>{children}</Hackle>
                </OverlayProvider>
                <Toaster />
              </NuqsAdapter>
            </TokenProvider>
          </OnlineUserInfoProvider>
        </JotaiProvider>
      </QueryClientProvider>
    </StackProvider>
  );
}
